/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { EDoc } from '@gh/builder-shared';
import {
  EDocSigningStatus,
  EDocType,
  QuotationBase,
  QuotationRestService,
  SalesFolderEDocRestService,
  SalesFolderRestService,
  SiteRestService,
} from '@gh/builder-shared-data';
import { DictionaryService, getRouteParam, InitViewService } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { GridLayoutService } from '@gh/core-ui-grid';

import { Observable } from 'rxjs';

import { RfqGeneral } from './lead.model';
import { RfqSiteCheckUsage, RfqSiteList, RfqSiteListItem, SalesFolderEDocSigningRequest } from './rfq.model';

@Injectable()
export class RfqBusinessService implements InitViewService {
  constructor(private sitesRest: SiteRestService,
              private salesFolderRest: SalesFolderRestService,
              private salesFolderEDocRest: SalesFolderEDocRestService,
              private gridLayoutService: GridLayoutService,
              private quotationsRest: QuotationRestService,
              private dictionaryService: DictionaryService) {

  }

  initView(route: ActivatedRouteSnapshot): any {
    const salesFolderId = getRouteParam(route, 'salesFolderId');

    return {
      salesFolder: this.salesFolderRest.getRfqGeneral(salesFolderId),
    };
  }

  initRfqGeneralTab(route: ActivatedRouteSnapshot): any {
    const salesFolderId = getRouteParam(route.parent!, 'salesFolderId'); // tslint:disable-line:no-non-null-assertion

    return {
      original: this.salesFolderRest.getRfqGeneral(salesFolderId),
    };
  }

  initRfqSitesTab(route: ActivatedRouteSnapshot): any {
    const salesFolderId = getRouteParam(route.parent!, 'salesFolderId'); // tslint:disable-line:no-non-null-assertion

    return {
      gridLayout: this.gridLayoutService.get('sites'),
      sitesList: this.sitesRest.findAll(salesFolderId),
    };
  }

  initRfqQuotationsTab(route: ActivatedRouteSnapshot): any {
    const salesFolderId = getRouteParam(route.parent!, 'salesFolderId'); // tslint:disable-line:no-non-null-assertion

    return {
      gridLayout: this.gridLayoutService.get('quotations'),
      quotationsItem: this.quotationsRest.findAllBySalesFolderId(salesFolderId),
      quotationBases: this.dictionaryService.getByEnum(QuotationBase),
      edocTypeDict: this.dictionaryService.getByEnum(EDocType),
    };
  }

  initRfqEDocListTab(route: ActivatedRouteSnapshot): any {
    const salesFolderId = getRouteParam(route.parent!, 'salesFolderId'); // tslint:disable-line:no-non-null-assertion

    return {
      gridLayout: this.gridLayoutService.get('rfqEDocs'),
      log: this.getEDocLog(salesFolderId),
      signingStatusDict: this.dictionaryService.getByEnum(EDocSigningStatus),
    };
  }

  getSiteData(sfId: ID): Observable<RfqSiteList> {
    return this.sitesRest.findAll(sfId);
  }

  siteCheckUsage(sfId: ID, site: RfqSiteListItem): Observable<RfqSiteCheckUsage> {
    return this.sitesRest.checkUsage(sfId, site);
  }

  removeSite(sfId: ID, siteId: ID): Observable<RfqSiteCheckUsage> {
    return this.sitesRest.remove(sfId, siteId);
  }

  updateRfqGeneral(rfq: RfqGeneral): Observable<RfqGeneral> {
    return this.salesFolderRest.updateRfqGeneral(rfq);
  }

  getEDocLog(salesFolderId: ID): Observable<EDoc[]> {
    return this.salesFolderEDocRest.getLog(salesFolderId);
  }

  sendQuoteToSign(salesFolderId: ID, quotationId: ID, signingRequest: SalesFolderEDocSigningRequest): Observable<void> {
    return this.salesFolderEDocRest.sendToSign(salesFolderId, quotationId, signingRequest);
  }
}
